import { useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle, ChevronDown, ChevronUp, Search, X, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { setUserRole, logAdminAction } from '../../lib/adminQueries';
import { useAuth } from '../../contexts/AuthContext';
import {
  DEFAULT_SORT_ASC,
  EMPTY_FILTERS,
  filterUsers,
  hasActiveFilters,
  sortUsers,
  type AdminUserEntry,
  type StatusFilter,
  type UserFilters,
  type UserSortKey,
} from '../../lib/adminUserView';
import { defaultSelection, isSelectable } from '../../lib/bulkEmail';
import UserStatusBadges from './UserStatusBadges';
import AdminUserDetail from './AdminUserDetail';
import AdminBulkEmailModal from './AdminBulkEmailModal';

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmClass: string;
  onConfirm: () => void;
}

function ConfirmModal({ state, onClose }: { state: ConfirmState; onClose: () => void }) {
  if (!state.open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900">{state.title}</h3>
            <p className="text-sm text-gray-600 mt-1">{state.message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={() => { state.onConfirm(); onClose(); }}
            className={`px-4 py-2 text-sm text-white rounded-md ${state.confirmClass}`}
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const EMPTY_CONFIRM: ConfirmState = {
  open: false,
  title: '',
  message: '',
  confirmLabel: 'Confirm',
  confirmClass: 'bg-red-600 hover:bg-red-700',
  onConfirm: () => {},
};

const COLUMNS: [UserSortKey, string][] = [
  ['name', 'Name / Email'],
  ['status', 'Status'],
  ['created_at', 'Joined'],
  ['last_active_at', 'Last Activity'],
  ['projects_created', 'Projects Created'],
  ['projects_completed', 'Projects Completed'],
];

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'ppu', label: 'PPU' },
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'ent', label: 'Ent' },
  { value: 'ent_admin', label: 'Ent (admin)' },
  { value: 'admin', label: 'Admin' },
  { value: 'locked', label: 'Locked' },
  { value: 'deleted', label: 'Deleted' },
];

const FILTER_INPUT_CLS =
  'w-full px-2 py-1 border border-gray-300 rounded text-xs font-normal focus:outline-none focus:ring-1 focus:ring-blue-500';

function DateFilterInput({ label, ariaLabel, value, onChange }: {
  label: 'From' | 'To';
  ariaLabel: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-1.5" title={ariaLabel}>
      <span className="w-8 flex-shrink-0 text-[10px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <input
        type="date"
        aria-label={ariaLabel}
        value={value}
        onChange={e => onChange(e.target.value)}
        className={FILTER_INPUT_CLS}
      />
    </label>
  );
}

// Budgeted against the failure that actually happens. When the origin is
// unreachable — no TLS, no connection — every attempt burns its full timeout and
// retrying cannot help, so the retry budget is the admin's staring-at-a-spinner
// budget. 2 x 12s + backoff caps that near 24s. Three 20s attempts would have
// been a full minute of silence before the error appeared.
const LOAD_TIMEOUT_MS = 12_000;
const LOAD_ATTEMPTS = 2;

/**
 * Fetches the users list, retrying transient network failures.
 *
 * The tab was observed failing with a bare "Failed to fetch" — the browser
 * rejecting the request outright, with no trace of it ever reaching the server.
 * A single dropped request should not leave an admin staring at an empty table,
 * so a network-level failure is retried with backoff. An answer from the server
 * is never retried, however badly it went: a 403 or a 500 is a real answer and
 * repeating it just delays the message.
 */
async function fetchUsersWithRetry(jwt: string): Promise<{ users: unknown[] }> {
  let lastNetworkError: unknown = null;

  for (let attempt = 1; attempt <= LOAD_ATTEMPTS; attempt++) {
    let resp: Response;
    try {
      resp = await fetch('/.netlify/functions/admin-users', {
        headers: { Authorization: `Bearer ${jwt}` },
        signal: AbortSignal.timeout(LOAD_TIMEOUT_MS),
      });
    } catch (e) {
      // Never reached the server, or took longer than the timeout allows.
      lastNetworkError = e;
      if (attempt < LOAD_ATTEMPTS) {
        await new Promise(r => setTimeout(r, 500 * 2 ** (attempt - 1)));
        continue;
      }
      break;
    }

    // The server answered. Parse defensively — a platform error page is HTML,
    // and a JSON parse failure on it would otherwise mask the real status code.
    let data: { users?: unknown[]; error?: string } = {};
    try {
      data = await resp.json();
    } catch {
      throw new Error(`Server returned ${resp.status} with an unreadable response.`);
    }

    if (!resp.ok) throw new Error(data.error || `Failed to load users (${resp.status}).`);
    return { users: data.users ?? [] };
  }

  const timedOut =
    lastNetworkError instanceof DOMException && lastNetworkError.name === 'TimeoutError';
  // Name the likely cause. Every other admin tab reads Supabase directly, on a
  // different origin — so this endpoint failing while the rest of the dashboard
  // works points at this site's own host, not at the database or your session.
  const hint =
    'Other admin tabs talk to the database directly, so they can keep working while this one cannot. ' +
    'If this persists, check the site host (Netlify) before suspecting the database.';
  throw new Error(
    timedOut
      ? `The users request timed out after ${LOAD_TIMEOUT_MS / 1000}s, twice. ${hint}`
      : `Could not reach the server after ${LOAD_ATTEMPTS} attempts. ${hint}`
  );
}

function formatLastActive(ts: string | null): string {
  if (!ts) return 'Never';
  return new Date(ts).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Header tick box. Indeterminate is set through a ref because React has no
 * prop for it, and a half-selected list that renders as empty invites a second
 * click that clears the selection the admin was building.
 */
function SelectAllBox({
  className,
  checked,
  indeterminate,
  onToggle,
}: {
  className?: string;
  checked: boolean;
  indeterminate: boolean;
  onToggle: () => void;
}) {
  return (
    <input
      type="checkbox"
      aria-label="Select all except test accounts"
      title="Selects everyone shown except test accounts. Test accounts can still be ticked one at a time."
      className={className}
      checked={checked}
      ref={el => {
        if (el) el.indeterminate = indeterminate;
      }}
      onChange={onToggle}
    />
  );
}

function RowBox({
  user,
  checked,
  onToggle,
}: {
  user: AdminUserEntry;
  checked: boolean;
  onToggle: () => void;
}) {
  const selectable = isSelectable(user);
  return (
    <input
      type="checkbox"
      aria-label={`Select ${user.email}`}
      title={selectable ? undefined : 'No address to send to, or the account is deleted'}
      disabled={!selectable}
      checked={checked}
      // The row itself opens the user detail page. Without this, ticking a box
      // would navigate away from the list being ticked.
      onClick={e => e.stopPropagation()}
      onChange={onToggle}
    />
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUserEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(EMPTY_CONFIRM);
  const [filters, setFilters] = useState<UserFilters>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<UserSortKey>('last_active_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const getJwt = async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? '';
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const jwt = await getJwt();
      // A missing token is worth naming: it means the session lapsed, and the
      // endpoint would answer a bare 401 that reads like a permissions problem.
      if (!jwt) throw new Error('Your session has expired. Sign in again to view users.');

      const data = await fetchUsersWithRetry(jwt);
      const loaded = data.users as AdminUserEntry[];
      setUsers(loaded);

      // A tick on somebody who has since been deleted would otherwise survive a
      // refresh and be sent to the server as a recipient.
      const live = new Set(loaded.map(u => u.id));
      setCheckedIds(prev => new Set([...prev].filter(id => live.has(id))));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const performAction = async (userId: string, action: string) => {
    const jwt = await getJwt();
    const resp = await fetch('/.netlify/functions/admin-users', {
      method: 'POST',
      headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, action }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || `Failed to ${action}`);
    load();
  };

  const closeConfirm = () => setConfirm(c => ({ ...c, open: false }));

  const handleRoleChange = (u: AdminUserEntry, newRole: 'user' | 'admin') => {
    setConfirm({
      open: true,
      title: `Set ${u.full_name || u.email} as ${newRole}?`,
      message: newRole === 'admin'
        ? 'This user will have full admin access including user management.'
        : 'This user will lose admin access.',
      confirmLabel: newRole === 'admin' ? 'Promote to Admin' : 'Remove Admin',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      onConfirm: async () => {
        await setUserRole(u.id, newRole);
        // The role change has already happened. logAdminAction throws on a
        // failed write, and when admin_audit_events was missing that throw
        // skipped load(), leaving the table showing the old role behind an
        // unhandled rejection. Log loudly, refresh regardless.
        try {
          await logAdminAction(user!.id, 'set_role', u.id, { email: u.email, newRole });
        } catch (err) {
          console.error('AUDIT WRITE FAILED for set_role — the role change did apply:', err);
        }
        load();
      },
    });
  };

  const handleLock = (u: AdminUserEntry) => {
    const name = u.full_name || u.email;
    setConfirm({
      open: true,
      title: `Lock ${name}?`,
      message: 'They will be unable to log in until unlocked. Their projects will not be affected.',
      confirmLabel: 'Lock User',
      confirmClass: 'bg-amber-600 hover:bg-amber-700',
      onConfirm: () => performAction(u.id, 'lock').catch(console.error),
    });
  };

  const handleUnlock = (u: AdminUserEntry) => {
    const name = u.full_name || u.email;
    setConfirm({
      open: true,
      title: `Unlock ${name}?`,
      message: 'They will regain full access to their account.',
      confirmLabel: 'Unlock User',
      confirmClass: 'bg-green-600 hover:bg-green-700',
      onConfirm: () => performAction(u.id, 'unlock').catch(console.error),
    });
  };

  const handleDelete = (u: AdminUserEntry) => {
    const name = u.full_name || u.email;
    setConfirm({
      open: true,
      title: `Delete ${name}?`,
      message: 'Their account will be permanently removed. Their projects will be preserved for records. This cannot be undone.',
      confirmLabel: 'Delete User',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: () => performAction(u.id, 'delete').catch(console.error),
    });
  };

  const handleReinstate = (u: AdminUserEntry) => {
    const name = u.full_name || u.email;
    setConfirm({
      open: true,
      title: `Reinstate ${name}?`,
      message: 'They will regain full access to their account and all their projects.',
      confirmLabel: 'Reinstate User',
      confirmClass: 'bg-green-600 hover:bg-green-700',
      onConfirm: () => performAction(u.id, 'reinstate').catch(console.error),
    });
  };

  const handleResetMfa = (u: AdminUserEntry) => {
    const name = u.full_name || u.email;
    setConfirm({
      open: true,
      title: `Reset two-factor authentication for ${name}?`,
      message: 'Verify their identity first (per the support runbook). Their authenticator and all backup codes are removed; they sign in with their password and must enroll a new authenticator.',
      confirmLabel: 'Reset MFA',
      confirmClass: 'bg-amber-600 hover:bg-amber-700',
      onConfirm: () => performAction(u.id, 'reset_mfa').catch(console.error),
    });
  };

  const setF = (patch: Partial<UserFilters>) => setFilters(f => ({ ...f, ...patch }));

  const toggleSort = (key: UserSortKey) => {
    if (sortKey === key) setSortAsc(a => !a);
    else { setSortKey(key); setSortAsc(DEFAULT_SORT_ASC[key]); }
  };

  const SortIcon = ({ k }: { k: UserSortKey }) =>
    sortKey === k
      ? (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
      : null;

  const visible = sortUsers(filterUsers(users, filters), sortKey, sortAsc);
  const selected = selectedId ? users.find(u => u.id === selectedId) : undefined;

  // Select all deliberately means "everyone here except the test accounts".
  // Ticking a fixture into the newsletter is never what anyone meant, but the
  // box stays live so a test account can still be picked on purpose — that is
  // exactly what you send to when checking that sending works.
  const selectAllIds = defaultSelection(visible);
  const allSelected = selectAllIds.length > 0 && selectAllIds.every(id => checkedIds.has(id));
  const someSelected = !allSelected && selectAllIds.some(id => checkedIds.has(id));

  const toggleOne = (id: string) =>
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setCheckedIds(prev => {
      const next = new Set(prev);
      for (const id of selectAllIds) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });


  if (selected) {
    return (
      <>
        <AdminUserDetail
          user={selected}
          onBack={() => setSelectedId(null)}
          onPromote={u => handleRoleChange(u, 'admin')}
          onDemote={u => handleRoleChange(u, 'user')}
          onLock={handleLock}
          onUnlock={handleUnlock}
          onDelete={handleDelete}
          onReinstate={handleReinstate}
          onResetMfa={handleResetMfa}
        />
        <ConfirmModal state={confirm} onClose={closeConfirm} />
      </>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Users</h2>
        <div className="flex items-center gap-2">
          {checkedIds.size > 0 && (
            <button
              onClick={() => setBulkOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              <Mail className="w-4 h-4" /> Email {checkedIds.size} selected
            </button>
          )}
          {hasActiveFilters(filters) && (
            <button
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4" /> Clear filters
            </button>
          )}
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading users…</div>
      ) : (
        <>
          {/* Mobile Table */}
          <div className="block md:hidden">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search name or email…"
                  value={filters.search}
                  onChange={e => setF({ search: e.target.value })}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={filters.status}
                onChange={e => setF({ status: e.target.value as StatusFilter })}
                className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {STATUS_FILTER_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-9" />
                  <col />
                  <col className="w-24" />
                  <col className="w-20" />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="pl-3 pr-1 py-2.5 text-left">
                      <SelectAllBox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onToggle={toggleAll}
                      />
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Name / Email</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-gray-400">No users found</td>
                    </tr>
                  ) : visible.map(u => (
                    <tr key={u.id} onClick={() => setSelectedId(u.id)} className="cursor-pointer hover:bg-blue-50">
                      <td className="pl-3 pr-1 py-3">
                        <RowBox
                          user={u}
                          checked={checkedIds.has(u.id)}
                          onToggle={() => toggleOne(u.id)}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-gray-900 leading-snug">{u.full_name || '—'}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </td>
                      <td className="px-2 py-3">
                        <UserStatusBadges user={u} />
                      </td>
                      <td className="px-2 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {u.created_at
                          ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                {visible.length} of {users.length} user{users.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-lg border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="pl-4 pr-1 py-3 w-9 text-left">
                      <SelectAllBox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onToggle={toggleAll}
                      />
                    </th>
                    {COLUMNS.map(([k, label]) => (
                      <th
                        key={k}
                        onClick={() => toggleSort(k)}
                        className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wide text-xs cursor-pointer hover:text-gray-700 select-none"
                      >
                        <span className="flex items-center gap-1">{label}<SortIcon k={k} /></span>
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-gray-50 border-t border-gray-200">
                    <td className="pl-4 pr-1 pb-3 pt-1" />
                    <td className="px-4 pb-3 pt-1 align-top">
                      <input
                        type="text"
                        aria-label="Filter by name or email"
                        placeholder="Search…"
                        value={filters.search}
                        onChange={e => setF({ search: e.target.value })}
                        className={FILTER_INPUT_CLS}
                      />
                    </td>
                    <td className="px-4 pb-3 pt-1 align-top">
                      <select
                        aria-label="Filter by status"
                        value={filters.status}
                        onChange={e => setF({ status: e.target.value as StatusFilter })}
                        className={FILTER_INPUT_CLS}
                      >
                        {STATUS_FILTER_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 pb-3 pt-1 align-top">
                      <div className="flex flex-col gap-1">
                        <DateFilterInput
                          label="From"
                          ariaLabel="Joined on or after"
                          value={filters.joinedFrom}
                          onChange={v => setF({ joinedFrom: v })}
                        />
                        <DateFilterInput
                          label="To"
                          ariaLabel="Joined on or before"
                          value={filters.joinedTo}
                          onChange={v => setF({ joinedTo: v })}
                        />
                      </div>
                    </td>
                    <td className="px-4 pb-3 pt-1 align-top">
                      <div className="flex flex-col gap-1">
                        <DateFilterInput
                          label="From"
                          ariaLabel="Active on or after"
                          value={filters.activeFrom}
                          onChange={v => setF({ activeFrom: v })}
                        />
                        <DateFilterInput
                          label="To"
                          ariaLabel="Active on or before"
                          value={filters.activeTo}
                          onChange={v => setF({ activeTo: v })}
                        />
                      </div>
                    </td>
                    <td className="px-4 pb-3 pt-1 align-top">
                      <input
                        type="number"
                        min={0}
                        aria-label="Minimum projects created"
                        placeholder="Min"
                        value={filters.minCreated}
                        onChange={e => setF({ minCreated: e.target.value })}
                        className={FILTER_INPUT_CLS}
                      />
                    </td>
                    <td className="px-4 pb-3 pt-1 align-top">
                      <input
                        type="number"
                        min={0}
                        aria-label="Minimum projects completed"
                        placeholder="Min"
                        value={filters.minCompleted}
                        onChange={e => setF({ minCompleted: e.target.value })}
                        className={FILTER_INPUT_CLS}
                      />
                    </td>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-400">No users found</td>
                    </tr>
                  ) : visible.map(u => (
                    <tr key={u.id} onClick={() => setSelectedId(u.id)} className="hover:bg-blue-50 cursor-pointer">
                      <td className="pl-4 pr-1 py-3">
                        <RowBox
                          user={u}
                          checked={checkedIds.has(u.id)}
                          onToggle={() => toggleOne(u.id)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{u.full_name || '—'}</p>
                        <p className="text-gray-500 text-xs">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <UserStatusBadges user={u} />
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {formatLastActive(u.last_active_at)}
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-center">{u.projects_created}</td>
                      <td className="px-4 py-3 text-gray-700 text-center">{u.projects_completed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              {visible.length} of {users.length} user{users.length !== 1 ? 's' : ''}
            </div>
          </div>
        </>
      )}

      <ConfirmModal state={confirm} onClose={closeConfirm} />

      {bulkOpen && (
        <AdminBulkEmailModal
          users={users}
          selectedIds={[...checkedIds]}
          onClose={didSend => {
            setBulkOpen(false);
            // Only a send clears the boxes. Cancelling has to leave the
            // selection intact, or a slip costs the whole list again.
            if (didSend) setCheckedIds(new Set());
          }}
        />
      )}
    </div>
  );
}
